import React, { useState, useEffect, useCallback, useMemo } from 'react';

interface Task {
  id: string;
  title: string;
  quadrant: string;
  status: string;
  priority: string;
  area: string;
  assignee: string;
  dueDate: string | null;
}

interface Project {
  id: string;
  name: string;
  tasksTotal: number;
  tasksOpen: number;
}

function Projects() {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTasks = useCallback(async () => {
    try {
      const response = await fetch('/api/tasks');
      if (!response.ok) {
        if (response.status === 401) {
          window.location.href = '/login';
          return;
        }
        throw new Error(`HTTP ${response.status}`);
      }
      const data = await response.json();
      setTasks(data.tasks || []);
      setError(data.error);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTasks();
    const interval = setInterval(fetchTasks, 30000); // Poll every 30s
    return () => clearInterval(interval);
  }, [fetchTasks]);

  // Group tasks by area to create "projects"
  const projects = useMemo(() => {
    const areaMap = new Map<string, { total: number; open: number }>();

    tasks.forEach(task => {
      const area = task.area || 'Uncategorized';
      const current = areaMap.get(area) || { total: 0, open: 0 };
      current.total++;
      if (task.status !== 'Done' && task.status !== 'Archived') {
        current.open++;
      }
      areaMap.set(area, current);
    });

    return Array.from(areaMap.entries()).map(([name, stats]) => ({
      id: name.toLowerCase().replace(/\s+/g, '-'),
      name,
      tasksTotal: stats.total,
      tasksOpen: stats.open,
    }));
  }, [tasks]);

  // Calculate totals
  const totalTasks = tasks.length;
  const openTasks = tasks.filter(t => t.status !== 'Done' && t.status !== 'Archived').length;

  return (
    <div>
      <header className="mb-8 flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold">Projects</h1>
          <p className="text-mc-muted mt-2">Tasks grouped by area from Notion</p>
        </div>
        <button
          onClick={fetchTasks}
          className="text-mc-accent text-sm hover:underline"
          disabled={loading}
        >
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
      </header>

      {error && (
        <div className="mb-6 p-4 bg-red-900/20 border border-red-500 rounded-lg text-red-400">
          Error loading tasks: {error}
        </div>
      )}

      {/* Project Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="card">
          {loading ? (
            <div className="animate-pulse bg-mc-border h-10 w-16 rounded"></div>
          ) : (
            <p className="text-4xl font-bold text-mc-accent">{projects.length}</p>
          )}
          <p className="text-mc-muted text-sm mt-2">Active Areas</p>
        </div>
        <div className="card">
          {loading ? (
            <div className="animate-pulse bg-mc-border h-10 w-16 rounded"></div>
          ) : (
            <p className="text-4xl font-bold text-mc-success">{totalTasks}</p>
          )}
          <p className="text-mc-muted text-sm mt-2">Total Tasks</p>
        </div>
        <div className="card">
          {loading ? (
            <div className="animate-pulse bg-mc-border h-10 w-16 rounded"></div>
          ) : (
            <p className="text-4xl font-bold text-mc-warning">{openTasks}</p>
          )}
          <p className="text-mc-muted text-sm mt-2">Open Tasks</p>
        </div>
      </div>

      {/* Project List */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="card animate-pulse">
              <div className="h-6 w-48 bg-mc-border rounded mb-4"></div>
              <div className="h-4 w-32 bg-mc-border rounded"></div>
            </div>
          ))}
        </div>
      ) : projects.length === 0 ? (
        <div className="card text-center py-8">
          <p className="text-mc-muted">No tasks found in Notion</p>
          <p className="text-sm text-mc-muted mt-2">
            Add tasks with an &quot;Area&quot; field to see them grouped here.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {projects.map((project) => (
            <div key={project.id} className="card">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-xl font-bold">{project.name}</h3>
                  <p className="text-mc-muted mt-1">
                    {project.tasksOpen} open tasks / {project.tasksTotal} total
                  </p>
                </div>
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                  project.tasksOpen === 0 ? 'bg-mc-success' : 'bg-mc-accent'
                } bg-opacity-20`}>
                  {project.tasksOpen === 0 ? 'Complete' : 'Active'}
                </span>
              </div>

              {/* Progress Bar */}
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span>Completion</span>
                  <span>{project.tasksTotal > 0 ? Math.round(((project.tasksTotal - project.tasksOpen) / project.tasksTotal) * 100) : 0}%</span>
                </div>
                <div className="w-full bg-mc-border rounded-full h-2">
                  <div
                    className={`rounded-full h-2 transition-all ${
                      project.tasksOpen === 0 ? 'bg-mc-success' : 'bg-mc-accent'
                    }`}
                    style={{ width: `${project.tasksTotal > 0 ? ((project.tasksTotal - project.tasksOpen) / project.tasksTotal) * 100 : 0}%` }}
                  ></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default Projects;
